"use client";

import { useMemo, type ComponentType } from "react";
import ArticleTOC from "@components/services/ArticleTOC";
import FAQList from "@components/services/FAQList";
import CtaBlock from "@components/services/CtaBlock";
import SectionHead from "@components/services/SectionHead";
import BriefingIntro from "./BriefingIntro";
import WasDanachBlock from "./WasDanachBlock";
import { useBriefingDraft } from "../hooks/useBriefingDraft";
import { isAnswered, isQuestionApplicable } from "../utils/questions";
import type { Answers, BriefingConfig, BriefingData, BriefingFormProps, Section } from "../types";

interface BriefingAppProps<F extends string> {
  data: BriefingData<F>;
  config: BriefingConfig;
  /**
   * The briefing's form binding (its side panel is the only thing that differs
   * between questionnaires). A client reference, so the server page can pass it.
   */
  FormComponent: ComponentType<BriefingFormProps<F>>;
}

type TocStatus = "empty" | "partial" | "complete";

function sectionStatus<F extends string>(section: Section<F>, answers: Answers): TocStatus {
  // Hidden dependsOn questions must not count — otherwise the section can
  // never reach "complete".
  const applicable = section.questions.filter((question) => isQuestionApplicable(question, answers));
  if (applicable.length === 0) return "complete";
  const anyAnswered = applicable.some((question) => isAnswered(answers[question.id]));
  if (!anyAnswered) return "empty";
  // Green = all *mandatory* questions answered; optional text fields never
  // block completion. Fallback to all-applicable for a section without any
  // required question.
  const required = applicable.filter((question) => question.required);
  const gate = required.length > 0 ? required : applicable;
  return gate.every((question) => isAnswered(answers[question.id])) ? "complete" : "partial";
}

export default function BriefingApp<F extends string>({
  data,
  config,
  FormComponent,
}: BriefingAppProps<F>) {
  const { answers, setAnswer, mergeAnswers, reset, hasHydrated, sentAt, changedSinceSent, markSent } =
    useBriefingDraft(config.storageKey);

  const tocItems = useMemo(
    () => [
      // Non-form entries carry "none": aligned with the tracked sections, but
      // no colour — a dot on them would read as a completion state they can't have.
      { id: "kurzantwort", label: "Überblick", status: "none" as const },
      ...data.sections.map((section) => ({
        id: section.id,
        label: data.navLabels[section.id] ?? section.title,
        // Before hydration every field reads as unanswered — showing "empty" for a
        // moment is correct (that's what the server rendered) and self-corrects
        // the instant the draft restores, so no loading flicker is needed.
        status: sectionStatus(section, answers),
      })),
      { id: "warum-json", label: "Nächste Schritte", status: "none" as const },
      { id: "faq", label: "FAQ", status: "none" as const },
    ],
    [data.sections, data.navLabels, answers]
  );

  return (
    <div className="article">
      <ArticleTOC items={tocItems} />
      <div>
        <BriefingIntro data={data} />

        <FormComponent
          data={data}
          answers={answers}
          onChange={setAnswer}
          onImport={mergeAnswers}
          onReset={reset}
          hasHydrated={hasHydrated}
          sentAt={sentAt}
          changedSinceSent={changedSinceSent}
          onMarkSent={markSent}
        />

        <WasDanachBlock data={data} />

        <section id="faq" className="py-16 border-t border-gray-100">
          <SectionHead eyebrow={data.faq.eyebrow} title={data.faq.title} blue />
          <FAQList items={data.faq.faqs} />
        </section>

        {data.finalCta ? (
          <section className="pb-8">
            <CtaBlock
              title={data.finalCta.title}
              description={data.finalCta.description}
              primaryCta={data.finalCta.primaryCta}
              secondaryCta={data.finalCta.secondaryCta}
            />
          </section>
        ) : null}
      </div>
    </div>
  );
}
